import React, { useState } from 'react';
import { CouponListProps, Coupon, getCouponStatus, formatCouponCode } from '../../types';
import Button from '../atoms/Button';
import Icon from '../atoms/Icon';
import './CouponList.css';

const CouponList: React.FC<CouponListProps> = ({
  coupons,
  onEdit,
  onDelete,
  onToggleStatus,
  loading = false
}) => {
  const [sortBy, setSortBy] = useState<'createdAt' | 'name' | 'code' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedCoupons = coupons
    .filter(coupon => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        coupon.code.toLowerCase().includes(term) ||
        coupon.name.toLowerCase().includes(term) ||
        (coupon.description && coupon.description.toLowerCase().includes(term))
      );
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'createdAt':
          aValue = a.createdAt.getTime();
          bValue = b.createdAt.getTime();
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'code':
          aValue = a.code.toLowerCase();
          bValue = b.code.toLowerCase();
          break;
        case 'status':
          aValue = getCouponStatus(a);
          bValue = getCouponStatus(b);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const getStatusBadge = (coupon: Coupon) => {
    const status = getCouponStatus(coupon);
    const statusConfig = {
      active: { label: 'Active', className: 'status-active' },
      inactive: { label: 'Inactive', className: 'status-inactive' },
      expired: { label: 'Expired', className: 'status-expired' },
      used_up: { label: 'Used Up', className: 'status-used-up' }
    };

    const config = statusConfig[status];
    return (
      <span className={`coupon-list__status ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatDiscount = (coupon: Coupon): string => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}% off`;
    } else {
      return `€${(coupon.discountValue / 100).toFixed(2)} off`;
    }
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="coupon-list">
        <div className="coupon-list__loading">
          <div className="coupon-list__spinner">
            <div className="coupon-list__spinner-circle"></div>
          </div>
          <p>Loading coupons...</p>
        </div>
      </div>
    );
  }

  if (coupons.length === 0) {
    return (
      <div className="coupon-list">
        <div className="coupon-list__empty">
          <div className="coupon-list__empty-icon">
            <Icon type="dollar" size="large" />
          </div>
          <h3 className="coupon-list__empty-title">No Coupons Found</h3>
          <p className="coupon-list__empty-description">
            Create your first coupon to start offering discounts to customers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="coupon-list">
      <div className="coupon-list__header">
        <div className="coupon-list__search">
          <div className="coupon-list__search-input">
            <Icon type="search" size="small" />
            <input
              type="text"
              placeholder="Search coupons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="coupon-list__search-field"
            />
          </div>
        </div>
        
        <div className="coupon-list__filters">
          <span className="coupon-list__count">
            {filteredAndSortedCoupons.length} coupon{filteredAndSortedCoupons.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="coupon-list__table">
        <div className="coupon-list__table-header">
          <button
            className={`coupon-list__header-cell ${sortBy === 'code' ? 'active' : ''}`}
            onClick={() => handleSort('code')}
          >
            Code
            {sortBy === 'code' && (
              <Icon type={sortOrder === 'asc' ? 'arrow' : 'arrow'} size="small" />
            )}
          </button>
          <button
            className={`coupon-list__header-cell ${sortBy === 'name' ? 'active' : ''}`}
            onClick={() => handleSort('name')}
          >
            Name
            {sortBy === 'name' && (
              <Icon type={sortOrder === 'asc' ? 'arrow' : 'arrow'} size="small" />
            )}
          </button>
          <div className="coupon-list__header-cell">Discount</div>
          <div className="coupon-list__header-cell">Usage</div>
          <button
            className={`coupon-list__header-cell ${sortBy === 'status' ? 'active' : ''}`}
            onClick={() => handleSort('status')}
          >
            Status
            {sortBy === 'status' && (
              <Icon type={sortOrder === 'asc' ? 'arrow' : 'arrow'} size="small" />
            )}
          </button>
          <button
            className={`coupon-list__header-cell ${sortBy === 'createdAt' ? 'active' : ''}`}
            onClick={() => handleSort('createdAt')}
          >
            Created
            {sortBy === 'createdAt' && (
              <Icon type={sortOrder === 'asc' ? 'arrow' : 'arrow'} size="small" />
            )}
          </button>
          <div className="coupon-list__header-cell">Actions</div>
        </div>

        <div className="coupon-list__table-body">
          {filteredAndSortedCoupons.map((coupon) => (
            <div key={coupon.id} className="coupon-list__row">
              <div className="coupon-list__cell coupon-list__cell--code">
                <span className="coupon-list__code">{coupon.code}</span>
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--name">
                <div className="coupon-list__name-info">
                  <span className="coupon-list__name">{coupon.name}</span>
                  {coupon.description && (
                    <span className="coupon-list__description">{coupon.description}</span>
                  )}
                </div>
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--discount">
                <span className="coupon-list__discount">{formatDiscount(coupon)}</span>
                {coupon.minimumOrderAmount && (
                  <span className="coupon-list__minimum">
                    Min: €{(coupon.minimumOrderAmount / 100).toFixed(2)}
                  </span>
                )}
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--usage">
                <span className="coupon-list__usage">
                  {coupon.usedCount}
                  {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                </span>
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--status">
                {getStatusBadge(coupon)}
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--created">
                <span className="coupon-list__date">{formatDate(coupon.createdAt)}</span>
              </div>
              
              <div className="coupon-list__cell coupon-list__cell--actions">
                <div className="coupon-list__actions">
                  <button
                    className="coupon-list__action coupon-list__action--edit"
                    onClick={() => onEdit(coupon)}
                    title="Edit coupon"
                  >
                    <Icon type="edit" size="small" />
                  </button>
                  
                  <button
                    className="coupon-list__action coupon-list__action--toggle"
                    onClick={() => onToggleStatus(coupon)}
                    title={coupon.isActive ? 'Deactivate coupon' : 'Activate coupon'}
                  >
                    <Icon type={coupon.isActive ? 'notification' : 'notification'} size="small" />
                  </button>
                  
                  <button
                    className="coupon-list__action coupon-list__action--delete"
                    onClick={() => onDelete(coupon)}
                    title="Delete coupon"
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CouponList;
